// GameController — input/render coordinator.
//
// Owns the live GameState and the bound DOM. Routes pointer + keyboard
// events into `dispatchFlick`, then re-renders. Pure presentation logic
// lives in render.ts; pure game logic lives in state.ts. This module is
// the only one allowed to know about DOM events.
//
// Implemented as a closure-factory rather than a class to save a few bytes
// in the bundle and keep `this`-binding out of event handlers.
//
// Phase 3 responsibilities (in addition to Phase 2's flick routing):
//   - Maintain an undo stack (cloneSnapshot on each successful flick).
//   - After each flick, run the win/lose predicate against the bar.
//   - Surface a status line + undo button below the bar, kept in sync.
//   - Block all input once status !== 'playing' (board cells re-render with
//     tabindex=-1 + aria-disabled; the dispatch guard is the runtime safety).

import { animateCascade, shouldReduceMotion } from './animator'
import { formatLocalDate } from './daily'
import { resolveDirection } from './input'
import {
  type BoardState,
  type CompletedRecord,
  SCHEMA_VERSION,
  deserializeBoardStateInto,
  deserializeCompletedInto,
  loadPersistedState,
  savePersistedState,
  serializeBoardState,
  serializeCompleted,
} from './persistence'
import type { Puzzle } from './puzzle'
import { type AppSettings, DEFAULT_SETTINGS } from './settings'
import { type LifetimeStats, emptyLifetimeStats } from './stats'
import {
  buildTokenDomLookup,
  measureCellSizePx,
  renderBar,
  renderBoard,
  renderGameOverBanner,
  renderUndoButton,
  shakeCell,
  type UndoButtonModel,
} from './render'
import { computeSlotStates } from './slotStates'
import {
  type Direction,
  type EncodedCell,
  type GameState,
  type GameStatus,
  MAX_UNDOS,
  dispatchFlick,
  hasAnyLegalFlick,
  initState,
} from './state'
import {
  cloneSnapshot,
  deserializeSnapshot,
  restoreSnapshot,
  serializeSnapshot,
  type GameSnapshot,
  type SerializedSnapshot,
} from './undo'

// Discriminated union — makes the illegal "both restore fields set" state
// unrepresentable. The four call paths are:
//   1. fresh game: omit `restore`
//   2. in-progress restore: { kind: 'inProgress', state }
//   3. completed restore: { kind: 'completed', record }
//   4. dev: omit `restore` + `persistEnabled: false`
// Widens GameStatus ('playing' | 'won' | 'lost') with the pre-mount 'idle'
// state. The controller's `status` getter returns 'idle' whenever no game is
// mounted (state === null). Consumed by the refresh coordinator's safe
// predicate, which treats anything other than 'playing' as disruptable.
export type GameStatusLike = 'idle' | GameStatus

export type RestoreSource =
  | {
      readonly kind: 'inProgress'
      readonly state: BoardState
      // Undo snapshots persisted alongside the in-progress board, oldest
      // first. Optional for back-compat with v5 saves written before undo
      // persistence existed: omit and the restored game has an empty undo
      // stack (the pre-fix behavior). New saves always populate it.
      readonly undoSnapshots?: readonly SerializedSnapshot[]
    }
  | { readonly kind: 'completed'; readonly record: CompletedRecord }

export interface MountOptions {
  readonly persistEnabled: boolean
  // Omit for a fresh game. With exactOptionalPropertyTypes, callers either
  // omit the key or pass a concrete RestoreSource — never `undefined`.
  readonly restore?: RestoreSource
  // Phase 5: called whenever the game enters a terminal state ('won' or 'lost'),
  // including the case where mount() is called with a `completed` restore.
  // Fires AT MOST ONCE per mount (the controller tracks whether it has already
  // fired for this state). The orchestrator wires this to show the endgame
  // modal. Optional so older call sites and the dev path don't need it.
  //
  // Phase 6: `source` distinguishes a live in-session terminal transition from
  // a transition that materialised purely because mount() was called with a
  // completed restore. The orchestrator updates lifetime stats only on 'live'
  // — otherwise reloading a finished game would double-count it.
  readonly onGameEnd?: (
    state: GameState,
    puzzle: Puzzle,
    source: 'live' | 'restored',
  ) => void
  // Phase 6: snapshot accessor for the lifetime stats blob. The controller
  // never owns stats — it only reads them to include alongside the per-puzzle
  // persistence payload. Pulled via callback (not a value prop) so the
  // orchestrator can mutate `currentStats` between successive flicks and the
  // controller always picks up the latest snapshot at persist time.
  readonly getStats?: () => LifetimeStats
  // Fires after every successful flick's render with the post-render state.
  // Hint evaluator in main.ts diffs against its prior snapshot to decide
  // whether a slot just turned blue or orange.
  readonly onBarChange?: (state: GameState, puzzle: Puzzle) => void
  // Fires when the player taps a word-bar slot. Slot index is 0-based
  // (matches state.exitedLetters indexing). Orchestrator decides what to do
  // — currently fires the explain-this-color hint toast.
  readonly onBarSlotTap?: (
    state: GameState,
    puzzle: Puzzle,
    slotIndex: number,
  ) => void
  // Fires after every flick's `completeRender` finishes — strictly AFTER
  // `onBarChange`. Retired demo-era hook (the demo's beat sequencer awaited
  // it); currently UNUSED — no mount passes it.
  readonly onAnimationComplete?: () => void
  // Warm-up gate ONLY. Optional prescribed-move filter, checked at the top of
  // the flick dispatch AFTER the status / isAnimating guards but BEFORE snapshot
  // cloning. Returns true to allow the move, false to reject it. A reject runs
  // the existing shakeCell feedback and returns immediately — no snapshot, no
  // undo-stack push, no event log, no onBarChange, no hint flags, no persist.
  // Undefined for daily / practice / dev ⇒ those modes execute exactly
  // today's code (the dispatch proceeds unconditionally).
  readonly moveFilter?: (source: EncodedCell, direction: Direction) => boolean
  // Warm-up gate ONLY. When true: (a) the undo-stack push on a successful flick
  // is skipped, and (b) onUndoClick is a no-op. With the stack permanently
  // empty, buildUndoModel's canUndo stays false so the undo button renders
  // disabled NATURALLY — no new render code. Undefined ⇒ today's behavior.
  readonly suppressUndo?: boolean
  // Warm-up gate ONLY (the undo-lesson screen). Fires after a successful undo
  // has restored the snapshot and completed its renders — the warm-up sequencer
  // advances its caption on it. Undefined for daily / practice / dev ⇒
  // those modes execute exactly today's code.
  readonly onUndo?: () => void
}

const DEFAULT_MOUNT_OPTIONS: MountOptions = { persistEnabled: true }

export interface GameController {
  mount(
    boardRoot: HTMLElement,
    barRoot: HTMLElement,
    bannerRoot: HTMLButtonElement,
    undoRoot: HTMLButtonElement,
    puzzle: Puzzle,
    options?: MountOptions,
  ): void
  unmount(): void
  // Programmatic input — routes through the same `onUndoClick` path that
  // pointer/keyboard input does, so persistence, callbacks, and undo all
  // behave identically to a human-driven action.
  //
  // RETIRED (demo-era, 2026-06-06): applyFlickProgrammatically — the scripted
  // flick the auto-demo drove — is commented out below (implementation + the
  // export entry). It is kept in SOURCE ONLY: comments never compile and
  // never ship in the bundle. To revive it (e.g. for an animated-hint or
  // replay-viewer feature), uncomment all three sites and re-add
  // `type DispatchResult` + `type TokenIdx` to the './state' import.
  //
  // applyFlickProgrammatically(
  //   tokenIdx: TokenIdx,
  //   direction: Direction,
  // ): DispatchResult
  applyUndoProgrammatically(): void
  // When locked, `onPointerDown` early-returns at the top, BEFORE pointer
  // capture. The keyboard `onKeyDown` path also early-returns. Used by the
  // loss animation (main.ts) so a stray tap can't fire between the status
  // transition and the modal opening.
  setInputLocked(locked: boolean): void
  // Swap the live `onBarChange` handler at runtime. Retired demo-era hook
  // (the demo swapped in a no-op for its duration so scripted flicks didn't
  // fire the hint-toast policy); currently UNUSED — kept small + harmless.
  setBarChangeHandler(
    fn: ((state: GameState, p: Puzzle) => void) | null,
  ): void
  getBarChangeHandler():
    | ((state: GameState, p: Puzzle) => void)
    | null
  // Install a one-shot animation-complete handler at runtime. Symmetric to
  // setBarChangeHandler. Retired demo-era hook (the demo installed a fresh
  // resolver before each scripted flick); currently UNUSED.
  setAnimationCompleteHandler(fn: (() => void) | null): void
  // True while the cascade animator is mid-playback.
  readonly isAnimating: boolean
  // Live game status, or 'idle' before any mount / after unmount.
  readonly status: GameStatusLike
  // True between pointerdown and pointerup/cancel — i.e. a flick gesture is in
  // progress. The refresh coordinator never re-mounts mid-drag.
  readonly isGestureActive: boolean
  // True once a flick or undo has landed on the current board (read from the
  // event log). The refresh coordinator gates a rollover/reload on INVESTMENT in
  // this board, not on status==='playing', so a fresh untouched board does not
  // block a new-day rollover.
  readonly hasCommittedMove: boolean
  // Epoch ms of the last flick/undo on the current board, or null if none yet
  // this mount. Feeds the coordinator's ~10-min abandoned-game grace window.
  readonly lastActivityAt: number | null
}

export function createGameController(): GameController {
  let state: GameState | null = null
  let puzzle: Puzzle | null = null
  let boardEl: HTMLElement | null = null
  let barEl: HTMLElement | null = null
  let bannerEl: HTMLButtonElement | null = null
  let undoEl: HTMLButtonElement | null = null
  // When false, persistCurrent is a no-op. Set by the dev mount path
  // so transient debugging puzzles never overwrite the player's real progress.
  let persistEnabled = true
  // Optional terminal-state callback (Phase 5). One-shot per mount — once fired,
  // remains gated until the next mount() call resets it. Phase 6 widened the
  // signature with a `source` discriminator (see MountOptions docs).
  let onGameEnd:
    | ((state: GameState, puzzle: Puzzle, source: 'live' | 'restored') => void)
    | null = null
  let gameEndFired = false
  // Phase 6: optional accessor for the orchestrator's current stats snapshot.
  let getStats: (() => LifetimeStats) | null = null
  let onBarChange: ((state: GameState, puzzle: Puzzle) => void) | null = null
  let onBarSlotTap:
    | ((state: GameState, puzzle: Puzzle, slotIndex: number) => void)
    | null = null

  // Phase 7: true while the cascade animator is mid-playback. Both
  // dispatchAndRender and onUndoClick short-circuit when set, preventing
  // overlapping cascades and undo races. Cleared in the animator's onComplete
  // callback. NOT the same as state.status — a terminal-state cascade is
  // still in motion AFTER state.status transitions to 'won' / 'lost', and
  // input must stay locked across the whole animation.
  let isAnimating = false

  // Timestamp (epoch ms) of the player's last INVESTMENT in the current board:
  // a successful flick or a successful undo. Null until the first such action
  // this mount (a fresh, untouched board has invested nothing). Feeds the
  // refresh coordinator's "invested in THIS board" gate so a new-day rollover
  // is deferred ONLY while a committed game is actively in progress (a move
  // within the grace window), never while the player merely stares at a fresh
  // board. Reset to null on every mount()/unmount().
  let lastActivityAt: number | null = null

  // Set by setInputLocked. When true, both onPointerDown and onKeyDown
  // early-return BEFORE any state capture, blocking player input from the
  // board surface entirely. The loss animation holds this true for its
  // duration (main.ts).
  let inputLocked = false

  // Fires after every successful flick's render — ordered AFTER onBarChange
  // inside completeRender. Retired demo-era hook; currently always null (no
  // mount passes onAnimationComplete and nothing calls the setter).
  let onAnimationComplete: (() => void) | null = null

  // Warm-up gate ONLY (both null/false outside warm-up). The prescribed-move
  // filter gates the dispatch path; suppressUndo skips the undo-stack push and
  // makes onUndoClick a no-op. See MountOptions for the contract.
  let moveFilter:
    | ((source: EncodedCell, direction: Direction) => boolean)
    | null = null
  let suppressUndo = false
  let onUndo: (() => void) | null = null

  // Snapshots captured *before* each successful flick. Pop on undo. Length
  // is bounded by the number of successful flicks since game start (or last
  // restart); not by MAX_UNDOS — burning an undo decrements its own counter,
  // not the stack depth.
  const undoStack: GameSnapshot[] = []

  // Pointer-capture bookkeeping. We track exactly one active gesture at a
  // time; secondary pointers (extra fingers, simultaneous mouse + touch) are
  // ignored until the active gesture ends.
  let activePointerId: number | null = null
  let activeSource: EncodedCell | null = null
  let startX = 0
  let startY = 0

  // Press-lift grab feedback: while a pointer is down on a letter tile the
  // cell gets .cell--pressed (scale + shadow + raised z-index, see board.css).
  // Pure grab confirmation — no directional cue, no color change, and it never
  // touches flick detection or the release-and-fly path. The lift-in eases via
  // a keyframes animation and the drop is instant on class removal, so the
  // lift produces NO transition events ever. That is a correctness invariant,
  // not a style choice: the cascade animator advances steps on the moving
  // cell's transitionend, so cells must never carry a CSS transition of their
  // own (a lift transition on the flicked cell once advanced the cascade
  // early, making struck tiles move in parallel with the flicker).
  let liftedCell: HTMLElement | null = null

  function pressLift(cell: HTMLElement): void {
    liftedCell = cell
    cell.classList.add('cell--pressed')
  }

  // All gesture ends (tap, rejected flick, legal flick, cancel, unmount): the
  // lift drops instantly. On a legal flick the flight animation owns the tile
  // from its first frame; on a rejected flick the shake plays as normal.
  function releaseLift(): void {
    if (liftedCell === null) return
    liftedCell.classList.remove('cell--pressed')
    liftedCell = null
  }

  function readCellEncoded(el: Element): EncodedCell | null {
    if (!(el instanceof HTMLElement)) return null
    const raw = el.dataset['cell']
    if (raw === undefined) return null
    const n = Number(raw)
    if (!Number.isFinite(n)) return null
    return n
  }

  function findCell(target: EventTarget | null): HTMLElement | null {
    if (!(target instanceof Element)) return null
    const el = target.closest('.cell')
    if (!(el instanceof HTMLElement)) return null
    // Only filled cells participate in input.
    if (el.dataset['filled'] !== 'true') return null
    return el
  }

  function onPointerDown(e: PointerEvent): void {
    if (inputLocked) return
    if (state !== null && state.status !== 'playing') return
    if (activePointerId !== null) return
    const cell = findCell(e.target)
    if (cell === null) return
    const cellEncoded = readCellEncoded(cell)
    if (cellEncoded === null) return

    activePointerId = e.pointerId
    activeSource = cellEncoded
    startX = e.clientX
    startY = e.clientY

    // Capture so subsequent move/up events are delivered to this cell even
    // if the pointer leaves it. Released automatically on pointerup.
    try {
      cell.setPointerCapture(e.pointerId)
    } catch {
      // Some environments throw if capture is unsupported; not fatal.
    }

    // Press-lift grab confirmation. Purely cosmetic — no effect on the flick
    // math captured above.
    pressLift(cell)
  }

  function clearActiveGesture(): void {
    activePointerId = null
    activeSource = null
    startX = 0
    startY = 0
  }

  function onPointerUp(e: PointerEvent): void {
    if (e.pointerId !== activePointerId) return
    const source = activeSource
    const sx = startX
    const sy = startY
    clearActiveGesture()
    if (source === null) return

    const dx = e.clientX - sx
    const dy = e.clientY - sy
    const direction = resolveDirection(dx, dy)
    if (direction === null) {
      // Sub-threshold; treat as tap — no move, just drop the lift.
      releaseLift()
      return
    }

    // dispatchAndRender drops the lift itself when the flick produces a legal
    // move (before the flight starts). For an illegal flick it does NOT, so
    // drop it here as the default; the shake plays as normal.
    dispatchAndRender(source, direction)
    releaseLift()
  }

  function onPointerCancel(e: PointerEvent): void {
    if (e.pointerId !== activePointerId) return
    clearActiveGesture()
    // Cancelled gesture is never a move — drop the lift.
    releaseLift()
  }

  function onKeyDown(e: KeyboardEvent): void {
    if (inputLocked) return
    // 'u'/'U' shortcut for undo. Handled before the arrow-key dispatch so the
    // shortcut works whenever a board cell has focus. After game over, all
    // cells render with tabindex=-1 — focus has fallen to <body>, so this
    // listener no longer fires and 'u' is inert (matches the disabled button).
    if (e.key === 'u' || e.key === 'U') {
      e.preventDefault()
      onUndoClick()
      return
    }

    let direction: 'UP' | 'DOWN' | 'LEFT' | 'RIGHT' | null = null
    switch (e.key) {
      case 'ArrowUp':
        direction = 'UP'
        break
      case 'ArrowDown':
        direction = 'DOWN'
        break
      case 'ArrowLeft':
        direction = 'LEFT'
        break
      case 'ArrowRight':
        direction = 'RIGHT'
        break
      default:
        return
    }
    const cell = findCell(e.target)
    if (cell === null) return
    const cellEncoded = readCellEncoded(cell)
    if (cellEncoded === null) return

    e.preventDefault() // prevent page scroll
    dispatchAndRender(cellEncoded, direction)
  }

  // Predicate run after every successful flick. Mutates `state.status` from
  // 'playing' to 'won' or 'lost' when the bar is full. The bar fills strictly
  // left-to-right, so checking the first wordLength entries is sufficient.
  function checkWinLose(target: GameState, puzzle: Puzzle): void {
    if (target.status !== 'playing') return
    const bar = target.exitedLetters
    const N = target.wordLength
    // Defensive lowercase — the corpus convention is lowercase, but a future
    // puzzle source could supply mixed-case data and silently mis-lose us.
    const targetLc = puzzle.targetWord.toLowerCase()

    // Win check: bar is full AND matches target letter-for-letter.
    if (bar.length >= N) {
      const filled = bar.slice(0, N).join('')
      if (filled === targetLc) {
        target.status = 'won'
        return
      }
    }

    // Lose check 1: undo cap exhausted AND any exited slot is non-canonical.
    // The three-way slot classification gives us the right semantic:
    //   - 'correct-canonical' — letter right, move keeps a win reachable (a
    //     verified valid-choice / DAG edge). Locked in; allows continuation.
    //   - 'correct-alt' (orange) — letter right but the move/cascade stranded
    //     the player. The puzzle is unsolvable for today's word from this
    //     state; no forward flick can recover it. With no undos left, end the
    //     game.
    //   - 'incorrect' (grey) — letter wrong for this slot. With no undos
    //     left, end the game (same as the prior letter-equality check).
    // Subsumes the bar-full-and-wrong case (the wrong slot would be N-1)
    // and catches early mistakes: any non-canonical slot at undo cap is a
    // guaranteed loss, so we end the session rather than force the player
    // to play it out.
    //
    // §4.1 atomicity: this reads the SAME gated source the live bar paints
    // from (computeSlotStates — DAG walk when live, per-slot union otherwise),
    // so the blue/orange boundary that decides playable-vs-lost is identical to
    // what the player sees. The board must never paint from the DAG while the
    // loss check reads the union, or vice versa.
    if (target.undoCount >= MAX_UNDOS) {
      const slotStates = computeSlotStates(target, puzzle)
      for (let i = 0; i < bar.length; i += 1) {
        const letter = bar[i]
        if (letter === undefined) continue
        const slotState = slotStates[i]
        if (slotState !== undefined && slotState !== 'correct-canonical') {
          target.status = 'lost'
          return
        }
      }
    }

    // Lose check 2: undo cap exhausted AND no legal flick remains. The player
    // cannot move and cannot undo, so the game is over even with a partial
    // (but correct so far) bar.
    if (target.undoCount >= MAX_UNDOS && !hasAnyLegalFlick(target)) {
      target.status = 'lost'
    }
  }

  function buildUndoModel(target: GameState): UndoButtonModel {
    const canUndo =
      target.status === 'playing' &&
      undoStack.length > 0 &&
      target.undoCount < MAX_UNDOS
    return {
      undoCount: target.undoCount,
      undoStackDepth: undoStack.length,
      maxUndos: MAX_UNDOS,
      canUndo,
    }
  }

  function renderAuxiliary(): void {
    if (state === null || bannerEl === null || undoEl === null || puzzle === null) return
    renderGameOverBanner(bannerEl, state, puzzle.targetWord)
    renderUndoButton(undoEl, buildUndoModel(state))
  }

  // Fire the onGameEnd callback exactly once per mount, when (and only when)
  // status has transitioned to a terminal value. Guarded so callers that
  // attach the listener AFTER a transition (e.g. mount with `completed`
  // restore) still see exactly one invocation.
  //
  // `source` distinguishes a live in-session transition (player's last flick
  // crossed the win/lose threshold) from a `restored` transition where mount()
  // hydrated an already-terminal state from localStorage. The orchestrator
  // uses this to gate the once-per-game lifetime-stats update.
  function maybeFireGameEnd(source: 'live' | 'restored'): void {
    if (gameEndFired) return
    if (state === null || puzzle === null) return
    if (state.status === 'playing') return
    gameEndFired = true
    if (onGameEnd !== null) onGameEnd(state, puzzle, source)
  }

  // Save current state to localStorage. Branches on game status so an
  // in-progress and a finished game persist to disjoint slots of the schema
  // (todayInProgress vs todayCompleted), preventing the modal from showing
  // for a still-playing puzzle on reload. No-op when persistence is disabled
  // (dev path) or before mount completes.
  function persistCurrent(): void {
    if (!persistEnabled) return
    if (state === null || puzzle === null) return
    // Pull the orchestrator's latest stats snapshot. Falls back to empty
    // stats if no accessor was wired — keeps the controller usable in tests
    // and dev paths that don't care about lifetime stats persistence.
    const stats: LifetimeStats = getStats !== null ? getStats() : emptyLifetimeStats()
    // Phase 7: preserve whatever settings are currently on disk so a flick
    // doesn't accidentally reset the player's dark-mode preference. Cheap
    // read-modify-write; settings rarely change relative to flicks.
    const onDisk = loadPersistedState()
    const settings: AppSettings = onDisk === null ? DEFAULT_SETTINGS : onDisk.settings
    // Every persist-from-live-play stamps the current local date so the
    // day-rollover detector can compare today's persisted date against
    // formatLocalDate(new Date()) on next mount.
    const todayLocalDate = formatLocalDate(new Date())
    if (state.status === 'playing') {
      savePersistedState({
        schemaVersion: SCHEMA_VERSION,
        todayPuzzleId: puzzle.id,
        todayLocalDate,
        todayInProgress: serializeBoardState(state),
        todayCompleted: null,
        stats,
        settings,
        // Persist the live undo stack so a Safari refresh mid-puzzle doesn't
        // strand the player with no usable undos. Oldest snapshot first;
        // restored in-order on next mount.
        todayUndoSnapshots: undoStack.map(serializeSnapshot),
      })
    } else {
      // First of the two-write pattern (plan §7): the controller writes the
      // completed record with the PREVIOUS stats. The orchestrator then runs
      // updateStatsOnGameEnd and writes again with the updated stats. Both
      // writes share the same key so the second clobbers the first cleanly.
      savePersistedState({
        schemaVersion: SCHEMA_VERSION,
        todayPuzzleId: puzzle.id,
        todayLocalDate,
        todayInProgress: null,
        todayCompleted: serializeCompleted(state),
        stats,
        settings,
      })
    }
  }

  function dispatchAndRender(source: EncodedCell, direction: 'UP' | 'DOWN' | 'LEFT' | 'RIGHT'): void {
    if (state === null || boardEl === null || barEl === null || puzzle === null) return
    // Hard guard: no input once the game is over.
    if (state.status !== 'playing') return
    // Phase 7: block during cascade playback. The data-animating attribute
    // on the board element disables pointer events at the CSS layer too;
    // this JS guard is the primary defence and the CSS is belt-and-suspenders.
    if (isAnimating) return

    // Warm-up gate ONLY: reject any move that isn't the current prescribed
    // move. Placed AFTER the status / isAnimating guards but BEFORE snapshot
    // cloning so a rejected move has ZERO side effects beyond the shake — no
    // snapshot, no undo-stack push, no event log, no onBarChange, no persist.
    // Undefined for every other mode ⇒ the dispatch proceeds unconditionally.
    if (moveFilter !== null && !moveFilter(source, direction)) {
      shakeCell(boardEl, source)
      return
    }

    // Look up the token at `source` BEFORE dispatch so we can re-focus the
    // moving token's new cell after the cascade (Phase 2 only does this for
    // the token that the user touched; cascaded tokens are not re-focused).
    const movingTokenIdx = state.cellToToken.get(source)

    // Snapshot BEFORE mutation so undo can restore the pre-flick position.
    // Discarded by GC if the dispatch fails.
    const pending = cloneSnapshot(state)

    const result = dispatchFlick(state, source, direction)

    if (!result.ok) {
      // Illegal flick — shake the source cell as feedback. No state mutation
      // happens on a failed dispatch, so we don't re-render the board. The
      // glyph springs back via the onPointerUp release() that follows.
      shakeCell(boardEl, source)
      return
    }

    // Legal move (release-and-fly): drop the press-lift NOW (instant, no
    // transition) so the flight owns the tile's transform from its first
    // frame. No-op for keyboard-driven dispatch (no active gesture).
    releaseLift()

    // Successful flick. Run the win/lose predicate before pushing onto the
    // undo stack so a winning flick doesn't accumulate an undo entry we'd
    // never use (cheap correctness; stack push is harmless either way).
    checkWinLose(state, puzzle)
    // Record investment in THIS board: a legal flick just landed. Set for ALL
    // successful flicks (even warm-up, where suppressUndo skips the stack push)
    // so the refresh coordinator's rollover gate sees the move. Only the
    // shake-and-return (illegal / no-op / warm-up-rejected) paths above DON'T
    // reach here, so they correctly leave investment untouched.
    lastActivityAt = Date.now()
    // Warm-up gate ONLY: suppressUndo keeps the undo stack permanently empty so
    // the undo button renders disabled naturally (buildUndoModel.canUndo stays
    // false). Every other mode pushes the snapshot as before.
    if (!suppressUndo) {
      undoStack.push(pending)
    }

    // Phase 9: enrich the just-pushed flick event with the in-game slot
    // classification so the share grid can render the same blue/orange
    // distinction the player saw. applyFlick pushes the event with letter +
    // slotIndex only; this is the first point in the dispatch path with the new
    // state.exitedProvenance entry in scope.
    //
    // §4.1/§11 atomicity (increment 5): reads the SAME gated source as the live
    // bar (computeSlotStates — DAG walk when live, per-slot union otherwise) so
    // the baked slotState equals what the bar painted this flick. The walk's
    // slot-i state depends only on moves 0..i, so a state baked at flick time
    // stays valid as later flicks land — no retroactive repaint. Without this
    // step the share grid collapses correct-canonical and correct-alt into a
    // single 🟦 square, hiding the wrong-move signal entirely.
    const lastIdx = state.eventLog.length - 1
    const lastEvent = state.eventLog[lastIdx]
    if (lastEvent !== undefined && lastEvent.type === 'flick') {
      const slot = lastEvent.slotIndex
      const slotStates = computeSlotStates(state, puzzle)
      const slotState = slotStates[slot]
      // applyFlick emits only flicks with defined letters, so the slot for this
      // event is filled and the gated source never returns 'empty' for it.
      if (slotState !== undefined && slotState !== 'empty') {
        state.eventLog[lastIdx] = { ...lastEvent, slotState }
      }
    }

    // Decide post-render focus target (used inside onComplete below). Captured
    // here so the closure sees the value computed against post-cascade state.
    const focusTokenIdx =
      state.status === 'playing' &&
      movingTokenIdx !== undefined &&
      state.tokenToCell.has(movingTokenIdx)
        ? movingTokenIdx
        : undefined
    // Re-bind to typed consts so the closure inside onComplete narrows the
    // mutable module-scope refs without TS complaining about reassignment.
    // Capturing the narrowed `state` (not null at this point) into a typed
    // const avoids the `as GameState` assertion the previous draft used —
    // same trick we use in main.ts for the DOM-landmark closures.
    const boardElLocal: HTMLElement = boardEl
    const barElLocal: HTMLElement = barEl
    const stateLocal: GameState = state
    const puzzleLocal: Puzzle = puzzle

    // The canonical post-cascade re-render — identical across reduced-motion
    // and animated paths. Defined as a closure so both branches dispatch
    // through the same code.
    //
    // Callback order is load-bearing: onBarChange fires BEFORE
    // onAnimationComplete (a retired demo-era ordering guarantee, kept stable
    // because onBarChange consumers like the warm-up sequencer rely on bar
    // state being current before any animation-complete signal).
    const completeRender = (): void => {
      renderBoard(boardElLocal, stateLocal, focusTokenIdx === undefined ? {} : { focusTokenIdx })
      renderBar(barElLocal, stateLocal, puzzleLocal)
      renderAuxiliary()
      persistCurrent()
      if (onBarChange !== null) onBarChange(stateLocal, puzzleLocal)
      maybeFireGameEnd('live')
      if (onAnimationComplete !== null) onAnimationComplete()
    }

    // Phase 7: cascade animation. The animator drives the tile transforms
    // and calls onComplete after the last step's transitionend. We hand it
    // a DOM lookup built from the PRE-flick board (still in the DOM — we
    // haven't called renderBoard yet) so the source cells line up.
    //
    // shouldReduceMotion gating lives INSIDE animateCascade, but we also
    // check here so we can skip the cellSize measurement + lookup-build cost
    // on a reduced-motion device.
    if (shouldReduceMotion()) {
      completeRender()
      return
    }

    const tokenDomLookup = buildTokenDomLookup(boardEl)
    const cellSize = measureCellSizePx(boardEl)
    if (cellSize === null) {
      // Board not yet laid out (zero-width). Skip animation, render canonical.
      completeRender()
      return
    }

    isAnimating = true
    boardEl.dataset['animating'] = 'true'
    animateCascade({
      boardRoot: boardEl,
      trajectory: result.trajectory,
      tokenDomLookup,
      cellSizePx: cellSize,
      onComplete: (): void => {
        isAnimating = false
        // Defensive: boardEl may have been nulled by an unmount during the
        // cascade (e.g. dev-next press mid-flick). Skip the cleanup +
        // canonical render if so — the new mount will have already rendered
        // the fresh board.
        if (boardEl === null) return
        delete boardEl.dataset['animating']
        completeRender()
      },
    })
  }

  function onBarClick(e: MouseEvent): void {
    if (barEl === null || state === null || puzzle === null) return
    if (onBarSlotTap === null) return
    const target = e.target
    if (!(target instanceof Element)) return
    const slot = target.closest('.slot')
    if (slot === null) return
    const slotIndex = Array.from(barEl.children).indexOf(slot)
    if (slotIndex === -1) return
    onBarSlotTap(state, puzzle, slotIndex)
  }

  function onUndoClick(): void {
    if (state === null || boardEl === null || barEl === null || puzzle === null) return
    // Warm-up gate ONLY: undo is a no-op (the stack is empty anyway, but the
    // 'u' keyboard shortcut bypasses the disabled button, so this is the
    // load-bearing guard). Every other mode falls through to the normal path.
    if (suppressUndo) return
    // All three conditions must hold. The button is disabled when any fails,
    // but the 'u' keyboard shortcut bypasses the disabled state — so the
    // guards here are load-bearing, not redundant.
    if (state.status !== 'playing') return
    if (state.undoCount >= MAX_UNDOS) return
    // Phase 7: undo is unanimated (no reverse-trajectory; cascade is
    // one-directional). Short-circuit during cascade playback so an undo can't
    // race a mid-flight cascade onComplete. The button is also disabled while
    // animating via the data-animating + the JS guard pattern.
    if (isAnimating) return
    const snapshot = undoStack.pop()
    if (snapshot === undefined) return

    restoreSnapshot(state, snapshot)
    state.undoCount += 1
    // APPEND-ONLY event log: undo events are recorded after the snapshot
    // restore so the share-grid generator can snapshot the bar at each undo.
    // restoreSnapshot does NOT touch state.eventLog (the snapshot doesn't
    // capture it) — phantom flick events from the popped move remain in the
    // log; sharegrid.ts walks the log forward and uses each flick's `letter`
    // field to reconstruct what the bar looked like at each undo.
    state.eventLog.push({ type: 'undo' })
    // Record investment: a successful undo is interaction with THIS board, the
    // same as a flick, so the rollover gate treats it as active play.
    lastActivityAt = Date.now()

    // Re-run win/lose detection: undoCount may have just reached MAX_UNDOS,
    // and the restored bar may contain an already-wrong letter committed
    // earlier. Keeps the post-undo and post-flick paths semantically aligned.
    checkWinLose(state, puzzle)

    renderBoard(boardEl, state)
    renderBar(barEl, state, puzzle)
    renderAuxiliary()
    persistCurrent()
    maybeFireGameEnd('live')
    // Warm-up gate ONLY: the undo-lesson screen advances on this. Fired LAST so
    // the restored board/bar are fully rendered before the sequencer reacts.
    if (onUndo !== null) onUndo()
  }

  function mount(
    boardRoot: HTMLElement,
    barRoot: HTMLElement,
    bannerRoot: HTMLButtonElement,
    undoRoot: HTMLButtonElement,
    puzzleArg: Puzzle,
    options: MountOptions = DEFAULT_MOUNT_OPTIONS,
  ): void {
    const newState = initState(puzzleArg)

    // Restore branches mutate newState in place AFTER initState has seeded
    // the immutable fields (puzzleId, letters, wordLength). Each branch is
    // the only writer for that path; the union discriminator makes them
    // mutually exclusive by construction (see RestoreSource).
    if (options.restore !== undefined) {
      switch (options.restore.kind) {
        case 'inProgress': {
          deserializeBoardStateInto(newState, options.restore.state)
          // Repopulate the undo stack from the persisted snapshots, if any.
          // undoStack was cleared in unmount(); pushing here happens BEFORE
          // the controller exposes any user interaction, so order is safe.
          const snapshots = options.restore.undoSnapshots
          if (snapshots !== undefined) {
            for (const s of snapshots) {
              undoStack.push(deserializeSnapshot(s))
            }
          }
          break
        }
        case 'completed':
          deserializeCompletedInto(newState, options.restore.record)
          break
      }
    }

    state = newState
    puzzle = puzzleArg
    persistEnabled = options.persistEnabled
    onGameEnd = options.onGameEnd ?? null
    getStats = options.getStats ?? null
    onBarChange = options.onBarChange ?? null
    onBarSlotTap = options.onBarSlotTap ?? null
    onAnimationComplete = options.onAnimationComplete ?? null
    moveFilter = options.moveFilter ?? null
    suppressUndo = options.suppressUndo ?? false
    onUndo = options.onUndo ?? null
    gameEndFired = false
    // Fresh board: zero investment until the player's first flick/undo.
    lastActivityAt = null
    boardEl = boardRoot
    barEl = barRoot
    bannerEl = bannerRoot
    undoEl = undoRoot

    renderBoard(boardEl, state)
    renderBar(barEl, state, puzzleArg)
    renderAuxiliary()

    boardEl.addEventListener('pointerdown', onPointerDown)
    boardEl.addEventListener('pointerup', onPointerUp)
    boardEl.addEventListener('pointercancel', onPointerCancel)
    boardEl.addEventListener('keydown', onKeyDown)
    undoEl.addEventListener('click', onUndoClick)
    barEl.addEventListener('click', onBarClick)

    // Fire onGameEnd after the initial render if we just restored a completed
    // state. Wrapped in a microtask via Promise.resolve so the caller sees the
    // controller return BEFORE the modal opens — keeps mount() observable
    // ordering predictable. Source is 'restored' so the orchestrator knows
    // NOT to re-run the lifetime-stats update for this game (it was already
    // counted when the player actually finished it).
    if (state.status !== 'playing') {
      Promise.resolve().then((): void => maybeFireGameEnd('restored'))
    }
  }

  function unmount(): void {
    if (boardEl !== null) {
      boardEl.removeEventListener('pointerdown', onPointerDown)
      boardEl.removeEventListener('pointerup', onPointerUp)
      boardEl.removeEventListener('pointercancel', onPointerCancel)
      boardEl.removeEventListener('keydown', onKeyDown)
    }
    // Drop any in-flight press-lift so an unmount mid-drag (e.g. dev-next)
    // doesn't leave a lifted cell behind.
    releaseLift()
    if (barEl !== null) {
      barEl.removeEventListener('click', onBarClick)
    }
    if (undoEl !== null) {
      undoEl.removeEventListener('click', onUndoClick)
    }
    boardEl = null
    barEl = null
    bannerEl = null
    undoEl = null
    state = null
    puzzle = null
    persistEnabled = true
    onGameEnd = null
    getStats = null
    onBarChange = null
    onBarSlotTap = null
    onAnimationComplete = null
    moveFilter = null
    suppressUndo = false
    onUndo = null
    gameEndFired = false
    inputLocked = false
    // Phase 7: clear the animation flag so a re-mount during cascade playback
    // (e.g. dev-next press mid-flick) starts from a clean unlocked state.
    // The animator's onComplete checks boardEl === null and bails out
    // gracefully if it fires after the unmount.
    isAnimating = false
    lastActivityAt = null
    undoStack.length = 0
    clearActiveGesture()
  }

  // RETIRED (demo-era, 2026-06-06): programmatic flick — the scripted flick
  // the auto-demo drove. Kept in SOURCE ONLY as the reference recipe for a
  // future animated-hint / replay-viewer feature; comments never compile and
  // never ship. To revive: uncomment here + the interface entry + the export
  // entry, and re-add `type DispatchResult` + `type TokenIdx` to the
  // './state' import.
  //
  // Routes through `dispatchAndRender` so the caller benefits from animation,
  // persistence, undo-stack bookkeeping, and the onBarChange /
  // onAnimationComplete callbacks the same way human input does. Returns a
  // synthetic DispatchResult so callers can branch on success / failure
  // without observing post-cascade state directly:
  //   - tokenNotFound: the token has already exited the board
  //   - dispatchAndRender refused (inputLocked / not playing / animating):
  //     surfaced as 'tokenNotFound', the closest existing reason, since no
  //     flick was applied. Callers should avoid these branches by never
  //     running while inputLocked, isAnimating, or status !== 'playing'.
  //
  // function applyFlickProgrammatically(
  //   tokenIdx: TokenIdx,
  //   direction: Direction,
  // ): DispatchResult {
  //   if (state === null) return { ok: false, reason: 'tokenNotFound' }
  //   const cell = state.tokenToCell.get(tokenIdx)
  //   if (cell === undefined) return { ok: false, reason: 'tokenNotFound' }
  //   // dispatchAndRender does not return its DispatchResult. To keep the
  //   // routing invariant ("through dispatchAndRender only"), we read the
  //   // exited count BEFORE dispatch and infer success after the fact via
  //   // the exitedLetters length delta.
  //   const exitedBefore = state.exitedLetters.length
  //   const tokenHadCellBefore = state.tokenToCell.has(tokenIdx)
  //   dispatchAndRender(cell, direction)
  //   // After dispatch (which may have been refused by guards, or shaken-and-
  //   // returned on an illegal flick), determine the outcome.
  //   if (state === null) return { ok: false, reason: 'tokenNotFound' }
  //   const exitedAfter = state.exitedLetters.length
  //   if (exitedAfter > exitedBefore) {
  //     const last = state.exitedLetters[exitedAfter - 1]
  //     if (last === undefined) {
  //       return { ok: false, reason: 'tokenNotFound' }
  //     }
  //     return {
  //       ok: true,
  //       exitedLetter: last,
  //       exitedTokenIdx: tokenIdx,
  //       // Trajectory is owned by the live animator + already consumed; an
  //       // empty trajectory keeps the type honest without leaking animator
  //       // internals.
  //       trajectory: [],
  //     }
  //   }
  //   // No exit happened. Either the dispatch was refused or the flick was
  //   // illegal. Distinguish by whether the token still occupies a cell — if
  //   // it does, the dispatch landed (illegal flick path: returned 'adjacent'
  //   // / 'noTokenBeyond'). Without re-running dispatchFlick we can't tell
  //   // which subreason; return 'noTokenBeyond' as a benign default.
  //   if (!tokenHadCellBefore) return { ok: false, reason: 'tokenNotFound' }
  //   return { ok: false, reason: 'noTokenBeyond' }
  // }

  function applyUndoProgrammatically(): void {
    onUndoClick()
  }

  function setInputLocked(locked: boolean): void {
    inputLocked = locked
  }

  function setBarChangeHandler(
    fn: ((s: GameState, p: Puzzle) => void) | null,
  ): void {
    onBarChange = fn
  }

  function getBarChangeHandler():
    | ((s: GameState, p: Puzzle) => void)
    | null {
    return onBarChange
  }

  function setAnimationCompleteHandler(fn: (() => void) | null): void {
    onAnimationComplete = fn
  }

  return {
    mount,
    unmount,
    // applyFlickProgrammatically, — RETIRED (demo-era); see the commented
    // implementation above for the revival recipe.
    applyUndoProgrammatically,
    setInputLocked,
    setBarChangeHandler,
    getBarChangeHandler,
    setAnimationCompleteHandler,
    get isAnimating(): boolean {
      return isAnimating
    },
    get status(): GameStatusLike {
      return state === null ? 'idle' : state.status
    },
    get isGestureActive(): boolean {
      return activePointerId !== null
    },
    // True once at least one flick or undo has landed on the current board.
    // Feeds the refresh coordinator's investment-based rollover/reload gate:
    // a fresh untouched board reads false, so a new-day rollover is not blocked
    // by the player merely staring at it.
    get hasCommittedMove(): boolean {
      return state !== null && state.eventLog.length > 0
    },
    // Epoch ms of the last flick/undo on the current board, or null if none yet
    // this mount. Paired with hasCommittedMove so the coordinator can apply the
    // ~10-min abandoned-game rule (defer rollover only while a committed game is
    // still actively in progress).
    get lastActivityAt(): number | null {
      return lastActivityAt
    },
  }
}
